import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { CustomShowcaseService } from '../../custom-showcase/services/custom-showcase.service';
import { setEntities } from '../../entity/actions/entity.actions';
import { FormatGroup } from '../../entity/models/entity';
import { isAuthorizedInKeycloak } from '../../keycloak/reducers/keycloak.reducer';
import { ListPaginationParams } from '../../list/models/list.dto';
import { ListService } from '../../list/services/list.service';
import { LibraryListItemsPayload } from '../actions/library-list.actions';
import * as LibraryListActions from '../actions/library-list.actions';
import * as LibraryListReducer from '../reducers/library-list.reducer';
import { getLibraryListItems, LibraryListTypes } from '../reducers/library-list.reducer';

@Injectable()
export class LibraryListEffects {
  public static showcaseItemsPrefetched = 10;
  private static readonly pageSize = 40;
  private static readonly sort = 'date.asc';

  public loadLibraryList$ = createEffect(() => this.actions$.pipe(
    ofType(LibraryListActions.loadLibraryList),
    switchMap(({id, libraryListType}) => {
      return (libraryListType === LibraryListTypes.LIST ? this.listService.getList(id) : this.customShowcaseService.getPublishedShowcaseById(id))
      .pipe(
        map((libraryList) => ({...libraryList, type: libraryListType})),
        switchMap((libraryList) => {
          const actions: Action[] = [];
          actions.push(LibraryListActions.loadLibraryListSuccess({libraryList}));

          const loadItemsPayload: LibraryListItemsPayload = {
            id: libraryList.id,
            sort: libraryList.sortBy || LibraryListEffects.sort,
            paginationParams: this.makePaginationParams(0, LibraryListEffects.pageSize),
            clearBeforeAdd: true,
            filterHiddenShowcaseItems: libraryList.type === LibraryListTypes.SHOWCASE,
            libraryListType
          };

          actions.push(LibraryListActions.loadLibraryListItems(loadItemsPayload));
          return actions;
        }),
        catchError((err: HttpErrorResponse) => {
          this.router.navigate(err.status === 404 ? ['/library-list/not-found'] : ['/library-list/server-error']);
          return of(LibraryListActions.loadLibraryListFailure({errorCode: err.status}));
        }),
      );
    }),
  ));

  public loadLibraryListItems$ = createEffect(() => this.actions$.pipe(
    ofType(LibraryListActions.loadLibraryListItems),
    concatLatestFrom(() => this.store.select(isAuthorizedInKeycloak)),
    mergeMap(([{id, sort, paginationParams, clearBeforeAdd, filterHiddenShowcaseItems, libraryListType}]) => {
      return (
        libraryListType === LibraryListTypes.LIST
          ? this.listService.getItems(id, sort, paginationParams, filterHiddenShowcaseItems)
          : this.customShowcaseService.getCustomShowcaseItems(id, paginationParams)
      )
      .pipe(
        withLatestFrom(this.store.select(getLibraryListItems)),
        switchMap(([listItemsDto, previouslyLoadedItems]) => {
          const {data, ...pagination} = listItemsDto;
          const actions: Action[] = [];
          actions.push(LibraryListActions.loadLibraryListItemsSuccess({pagination}));
          if (clearBeforeAdd) {
            actions.push(LibraryListActions.clearLibraryListItems());
          }
          actions.push(LibraryListActions.addLoadedLibraryListItems({entities: data}));
          actions.push(setEntities({entities: [...previouslyLoadedItems, ...data] as FormatGroup[]}));

          return actions;
        }),
        catchError((err: HttpErrorResponse) =>
          of(LibraryListActions.loadLibraryListItemsFailure({errorCode: err.status}))),
      );
    }),
  ));

  public loadMoreLibraryListItems$ = createEffect(() => this.actions$.pipe(
    ofType(LibraryListActions.loadMoreLibraryListItems),
    concatLatestFrom(() => this.store.select(LibraryListReducer.getItemsPagination)),
    concatLatestFrom(() => this.store.select(LibraryListReducer.getLibraryList)),
    filter(([[, pagination]]) => !(pagination.page >= pagination.totalPages - 1)),
    map(([[, pagination], {id, type, sortBy}]) => LibraryListActions.loadLibraryListItems({
      id: id,
      sort: sortBy || LibraryListEffects.sort,
      paginationParams: this.makePaginationParams(pagination.page + 1, LibraryListEffects.pageSize),
      filterHiddenShowcaseItems: type === LibraryListTypes.SHOWCASE,
      libraryListType: type
    })),
  ));

  constructor(
    private readonly store: Store,
    private readonly actions$: Actions,
    private readonly customShowcaseService: CustomShowcaseService,
    private readonly listService: ListService,
    private readonly router: Router,
  ) {
  }

  private makePaginationParams = (pageNum = 0, pageSize = 999): ListPaginationParams => {
    return {pageNum, pageSize};
  };
}
